/**
 * Top-left debug overlay, toggled with backtick (`).
 * Shows FPS/TPS, player logical + visual position, stance, stamina, and
 * move state. Phase-1 skeleton; phase-2+ will layer vision cones, sound
 * flood regions, and awareness meters via Pixi overlays on top.
 */

import type { Guard } from '../sim/guard';
import type { FloorPatrol } from '../sim/patrol';
import type { Player } from '../sim/player';

export class DebugOverlay {
  private readonly root: HTMLElement;
  private visible = false;
  private fpsEma = 0;
  private tickAcc = 0;
  private windowAcc = 0;
  private lastTps = 0;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('debug-overlay');
    this.setVisible(false);
  }

  isVisible(): boolean {
    return this.visible;
  }

  toggle(): void {
    this.setVisible(!this.visible);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.root.style.display = v ? 'block' : 'none';
  }

  update(
    dtSec: number,
    ticksRan: number,
    player: Player,
    guardCount = 0,
    floor?: FloorPatrol,
    guards: readonly Guard[] = [],
  ): void {
    // Accumulate counters even while hidden so TPS is ready the moment the
    // user toggles the overlay on.
    const fps = dtSec > 0 ? 1 / dtSec : 0;
    this.fpsEma = this.fpsEma === 0 ? fps : this.fpsEma * 0.9 + fps * 0.1;
    this.tickAcc += ticksRan;
    this.windowAcc += dtSec;
    if (this.windowAcc >= 1) {
      this.lastTps = this.tickAcc / this.windowAcc;
      this.tickAcc = 0;
      this.windowAcc = 0;
    }

    if (!this.visible) return;

    const moving = player.moveDx !== 0 || player.moveDy !== 0;
    let patrolText = '';
    if (floor) {
      const cap = floor.captainId !== null ? guards.find((g) => g.id === floor.captainId && !g.body.dead) : undefined;
      patrolText = `\ncaptain ${cap ? `#${cap.id}` : 'none'}`;
      for (const rp of floor.rooms) {
        const live = guards.filter((g) => !g.body.dead && !g.isCaptain && g.homeRoomId === rp.roomId).length;
        patrolText += `\n${rp.name}  ${live}/${rp.expectedCount}`;
      }
    }
    this.root.textContent =
      `FPS ${this.fpsEma.toFixed(1)}  TPS ${this.lastTps.toFixed(1)}\n` +
      `tile   (${player.x}, ${player.y})\n` +
      `pos    (${player.fx.toFixed(3)}, ${player.fy.toFixed(3)})\n` +
      `stance ${player.stance}${player.exhausted ? '  (exhausted)' : ''}\n` +
      `stamina ${player.stamina.toFixed(1)}\n` +
      `face  (${player.facingDx}, ${player.facingDy})\n` +
      `move  ${moving ? `dir(${player.moveDx},${player.moveDy})` : 'idle'}\n` +
      `guards ${guardCount}` +
      patrolText;
  }
}
